import React from "react";
import {
  Ship,
  Receipt,
  LogOut,
  Building2,
  Users,
  BarChart2,
  Settings,
  Shield,
  ShieldAlert,
  PieChart,
  Package,
  ClipboardList,
  MapPinned,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { Avatar } from "../ui/Avatar";

// nav key → required permission key(s). Any match = visible.
const CLIENT_NAV_PERMISSIONS = {
  dashboard: ["view_dashboard"],
  shipments: ["view_shipments"],
  products:  ["view_products"],
  orders:    ["view_orders"],
  invoices:  ["view_invoices"],
  reporting: ["view_reporting"],
  claims:    ["view_claims"],
  users:     ["view_users", "manage_users"],
};

function useNavItems(user, hasPermission) {
  const { t } = useTranslation();

  const ADMIN_NAV = [
    { key: "dashboard", label: t("nav.dashboard"), Icon: BarChart2 },
    { key: "shipments", label: t("nav.shipments"),  Icon: Ship },
    { key: "products",  label: t("nav.products"),   Icon: Package },
    { key: "orders",    label: t("nav.orders"),     Icon: ClipboardList },
    { key: "catalogs",  label: t("nav.catalogs"),   Icon: MapPinned },
    { key: "invoices",  label: t("nav.invoices"),   Icon: Receipt },
    { key: "reporting", label: t("nav.reporting"),  Icon: PieChart },
    { key: "claims",    label: t("nav.claims"),     Icon: ShieldAlert },
    { key: "users",     label: t("nav.clients"),    Icon: Building2 },
    { key: "roles",     label: t("nav.roles"),      Icon: Shield },
    { key: "settings",  label: t("nav.settings"),   Icon: Settings },
  ];

  const ALL_CLIENT_NAV = [
    { key: "dashboard", label: t("nav.dashboard"), Icon: BarChart2 },
    { key: "shipments", label: t("nav.shipments"), Icon: Ship },
    { key: "products",  label: t("nav.products"),  Icon: Package },
    { key: "orders",    label: t("nav.orders"),    Icon: ClipboardList },
    { key: "catalogs",  label: t("nav.catalogs"),  Icon: MapPinned },
    { key: "invoices",  label: t("nav.invoices"),  Icon: Receipt },
    { key: "reporting", label: t("nav.reporting"), Icon: PieChart },
    { key: "claims",    label: t("nav.claims"),    Icon: ShieldAlert },
    { key: "users",     label: t("nav.users"),     Icon: Users },
  ];

  const byPermission = (items) => items.filter(({ key }) => {
    const required = CLIENT_NAV_PERMISSIONS[key];
    return required ? required.some((p) => hasPermission(p)) : true;
  });

  if (user?.company?.type === "ADMIN") return byPermission(ADMIN_NAV);

  return byPermission(ALL_CLIENT_NAV);
}

export function Sidebar({ active, onNavigate, user, onLogout }) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const navItems = useNavItems(user, hasPermission);

  const fullName = user?.fullname || "—";
  const initials = user?.initials?.toUpperCase() ||
    fullName.split(" ").map((w) => w[0]).filter(Boolean).join("").slice(0, 2).toUpperCase() ||
    "?";
  const roleKey = user?.role?.name ?? (typeof user?.role === 'string' ? user?.role : '');
  const roleLabel = t(`roles.${roleKey}`, { defaultValue: roleKey });
  const isGlobalAdmin = user?.company?.type === "ADMIN";

  return (
    <aside className="w-60 bg-primary text-white flex flex-col h-screen sticky top-0 z-30 flex-shrink-0">
      {/* Logo */}
      <div className="px-5 pt-5 pb-4 border-b border-white/10">
        <div className="flex items-center gap-2.5">
          <div className="w-12 h-9 rounded-md bg-white grid place-items-center overflow-hidden p-0.5 flex-shrink-0">
            <img
              src="/mfb-logo.png"
              alt="MFB"
              className="w-full h-full object-contain"
            />
          </div>
          <div>
            <div className="font-semibold text-[14px] tracking-tight">{t("brand.name")}</div>
            <div className="text-[10.5px] text-sky-200">Fresh Logistics</div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-3 overflow-y-auto text-[13px]">
        {navItems.length > 0 && (
          <>
            <div className="text-[10px] uppercase tracking-wider text-sky-300/70 px-2 pt-2 pb-1.5 font-semibold">
              {isGlobalAdmin ? t("nav.section_admin") : t("nav.section_client")}
            </div>
            {navItems.map(({ key, label, Icon, badge }) => {
              const isActive = active === key;
              return (
                <button
                  key={key}
                  onClick={() => onNavigate?.(key)}
                  className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md font-medium mb-0.5 transition text-left ${
                    isActive
                      ? "bg-white/15 text-white"
                      : "text-sky-100/80 hover:bg-white/5 hover:text-white"
                  }`}
                >
                  <Icon size={15} strokeWidth={1.75} className="flex-shrink-0" />
                  <span className="flex-1">{label}</span>
                  {badge != null && (
                    <span className="bg-accent text-white text-[9px] font-bold px-1 py-0.5 rounded-full">
                      {badge}
                    </span>
                  )}
                </button>
              );
            })}
          </>
        )}
      </nav>

      {/* User footer */}
      <div className="border-t border-white/10 px-3 py-3 flex items-center gap-2.5">
        <Avatar initials={initials} size="sm" />
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-semibold text-white truncate">{fullName}</p>
          <p className="text-[10px] text-white/50 truncate">{roleLabel}</p>
        </div>
        <button
          onClick={onLogout}
          title={t("nav.logout")}
          className="w-7 h-7 grid place-items-center rounded-md text-white/50 hover:bg-white/10 hover:text-white transition flex-shrink-0"
        >
          <LogOut size={14} strokeWidth={1.75} />
        </button>
      </div>
    </aside>
  );
}

export default Sidebar;
